import { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Alert,
  Skeleton,
  Chip,
  Button,
  Stack,
  CircularProgress,
  Snackbar,
} from "@mui/material";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import EmptyState from "../components/EmptyState";
import ResponsiveTableContainer from "../components/ResponsiveTableContainer";
import {
  getBillingHistory,
  resendReceipt,
  type BillingHistoryResponse,
  type InvoiceItem,
} from "../api/dashboard";

const PAGE_SIZE = 20;

function formatCents(cents: number): string {
  return `$${(cents / 100).toFixed(2)}`;
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

const STATUS_LABEL: Record<InvoiceItem["status"], string> = {
  paid: "Paid",
  failed: "Failed",
  pending: "Pending",
  refunded: "Refunded",
};

const STATUS_COLOR: Record<
  InvoiceItem["status"],
  "success" | "error" | "warning" | "default"
> = {
  paid: "success",
  failed: "error",
  pending: "warning",
  refunded: "default",
};

const TYPE_LABEL: Record<InvoiceItem["type"], string> = {
  subscription: "Subscription",
  overage: "Overage",
  one_time: "One-time",
};

interface ToastState {
  kind: "success" | "error";
  text: string;
}

export default function BillingHistoryPage() {
  const [data, setData] = useState<BillingHistoryResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<ToastState | null>(null);
  const [resendingId, setResendingId] = useState<number | null>(null);
  const [page, setPage] = useState(1);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    getBillingHistory({ page, pageSize: PAGE_SIZE })
      .then((res) => {
        if (cancelled) return;
        setData(res);
        setError(null);
      })
      .catch(() => {
        if (cancelled) return;
        setError("Couldn't load payment history.");
      })
      .finally(() => {
        if (cancelled) return;
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [page]);

  async function handleResend(invoice: InvoiceItem) {
    if (resendingId !== null) return;
    setResendingId(invoice.id);
    try {
      await resendReceipt(invoice.id);
      setToast({ kind: "success", text: "Receipt resent — check your inbox." });
    } catch (err: unknown) {
      const status =
        err && typeof err === "object" && "response" in err
          ? (err as { response?: { status?: number } }).response?.status
          : undefined;
      if (status === 429) {
        setToast({
          kind: "error",
          text: "Receipt resend limit reached for this invoice. Try again tomorrow.",
        });
      } else if (status === 502) {
        setToast({
          kind: "error",
          text: "Couldn't send right now — please try again in a moment.",
        });
      } else if (status === 409) {
        setToast({
          kind: "error",
          text: "This invoice isn't paid yet — no receipt to resend.",
        });
      } else {
        setToast({ kind: "error", text: "Couldn't resend receipt." });
      }
    } finally {
      setResendingId(null);
    }
  }

  const totalPages = data
    ? Math.max(1, Math.ceil(data.total / data.pageSize))
    : 1;

  return (
    <Box>
      <Typography variant="h5" component="h1" gutterBottom>
        Payment history
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {loading && !data ? (
        <Skeleton variant="rounded" height={300} />
      ) : data && data.total === 0 ? (
        <EmptyState
          icon={
            <ReceiptLongIcon
              aria-hidden="true"
              fontSize="large"
              color="disabled"
            />
          }
          title="No payments yet"
          description="Charges will appear here once you have a paid subscription."
        />
      ) : data ? (
        <>
          <ResponsiveTableContainer>
            <Box
              component="table"
              sx={{
                width: "100%",
                borderCollapse: "collapse",
                fontSize: "0.875rem",
                "& th, & td": {
                  py: 1.25,
                  px: 1,
                  textAlign: "left",
                },
                "& thead th": {
                  color: "text.secondary",
                  fontWeight: 500,
                  borderBottom: 1,
                  borderColor: "divider",
                },
                "& tbody tr": {
                  borderBottom: 1,
                  borderColor: "divider",
                },
                "& .num": { textAlign: "right" },
              }}
            >
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Type</th>
                  <th>Status</th>
                  <th className="num">Amount</th>
                  <th>Period</th>
                  <th className="num">Action</th>
                </tr>
              </thead>
              <tbody>
                {data.invoices.map((inv) => (
                  <tr key={inv.id}>
                    <td>{formatDate(inv.createdAt)}</td>
                    <td>{TYPE_LABEL[inv.type]}</td>
                    <td>
                      <Chip
                        label={STATUS_LABEL[inv.status]}
                        size="small"
                        color={STATUS_COLOR[inv.status]}
                        variant="outlined"
                      />
                    </td>
                    <td className="num">{formatCents(inv.amountCents)}</td>
                    <td>
                      {formatDate(inv.periodStart)} –{" "}
                      {formatDate(inv.periodEnd)}
                    </td>
                    <td className="num">
                      {inv.status === "paid" && (
                        <Button
                          size="small"
                          variant="text"
                          disabled={resendingId !== null}
                          onClick={() => handleResend(inv)}
                          startIcon={
                            resendingId === inv.id ? (
                              <CircularProgress size={14} color="inherit" />
                            ) : undefined
                          }
                        >
                          {resendingId === inv.id
                            ? "Sending..."
                            : "Resend receipt"}
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Box>
          </ResponsiveTableContainer>

          {totalPages > 1 && (
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              sx={{ mt: 2 }}
            >
              <Button
                size="small"
                disabled={page === 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
              >
                ← Previous
              </Button>
              <Typography variant="body2" color="text.secondary">
                Page {page} of {totalPages}
              </Typography>
              <Button
                size="small"
                disabled={page === totalPages}
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              >
                Next →
              </Button>
            </Stack>
          )}
        </>
      ) : null}

      <Snackbar
        open={toast !== null}
        autoHideDuration={5000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        {toast ? (
          <Alert
            severity={toast.kind}
            onClose={() => setToast(null)}
            variant="filled"
            sx={{ width: "100%" }}
          >
            {toast.text}
          </Alert>
        ) : undefined}
      </Snackbar>
    </Box>
  );
}
